import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CKWysiwyg from '../CKWysiwyg/CKWysiwyg';
import { Utils } from '../../services';
import { cloneDeep } from 'es-toolkit';

import './TranslatableWysiwyg.css';

/**
 * Language maps must stay plain objects. An array is truthy, so assigning
 * `text.fr = html` survives in memory but JSON.stringify drops it (`[]`).
 */
const normalize_translatable = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value))
        return cloneDeep(value);
    return {};
};

const map_is_empty = (value, langs) => langs.every((lang) => !value?.[lang]);

const TranslatableWysiwyg = (props) => {
    const {
        defaultValue, onChange,
        withShortcodes, withColors, withImages, withTables,
        original, readOnly, languages: languages_override,
        textColor, linksColor, initialLang,
    } = props;

    const langs = useMemo(
        () => {
            if (languages_override?.length)
                return languages_override;
            return original ? ['original'].concat(Utils.langs()) : Utils.langs();
        },
        [languages_override, original]
    );
    const resolve_initial_lang = useCallback(() => {
        if (initialLang && langs.includes(initialLang))
            return initialLang;
        return langs[0];
    }, [initialLang, langs]);
    const [selectedLang, setSelectedLang] = useState(resolve_initial_lang);
    const [inputText, setInputText]       = useState(() => normalize_translatable(defaultValue));
    const on_change_ref                   = useRef(onChange);
    const last_emitted_ref                = useRef(normalize_translatable(defaultValue));
    // Remounting CKEditor (key=lang) can emit an empty change for the language
    // being left. Ignore that burst so French is still there when coming back.
    const ignore_editor_change            = useRef(false);

    useEffect(() => {
        on_change_ref.current = onChange;
    }, [onChange]);

    const select_lang = useCallback((lang) => {
        if (!lang || lang === selectedLang)
            return;
        ignore_editor_change.current = true;
        setSelectedLang(lang);
        window.setTimeout(() => {
            ignore_editor_change.current = false;
        }, 0);
    }, [selectedLang]);

    useEffect(() => {
        select_lang(resolve_initial_lang());
    }, [resolve_initial_lang]); // eslint-disable-line react-hooks/exhaustive-deps -- seed from URL / preview lang only

    const serialize_translatable = useCallback((value) => {
        const obj = normalize_translatable(value);
        const normalized = {};
        langs.forEach(l => {
            normalized[l] = obj[l] ?? '';
        });
        return JSON.stringify(normalized);
    }, [langs]);

    useEffect(() => {
        const incoming = normalize_translatable(defaultValue);
        const incoming_serialized = serialize_translatable(incoming);
        const emitted = normalize_translatable(last_emitted_ref.current);
        if (incoming_serialized === serialize_translatable(emitted))
            return;
        // Parent echoed [] / {} while this editor already has text.
        if (map_is_empty(incoming, langs) && !map_is_empty(emitted, langs))
            return;
        last_emitted_ref.current = incoming;
        setInputText(incoming);
    }, [defaultValue, langs, serialize_translatable]);

    const notify_change = useCallback((lang, value) => {
        setInputText((prev) => {
            const temp = normalize_translatable(prev);
            temp[lang] = value ?? '';
            last_emitted_ref.current = temp;
            window.setTimeout(() => {
                on_change_ref.current?.(temp);
            }, 0);
            return temp;
        });
    }, []);

    const editor_on_change = useMemo(
        () => (value) => {
            if (ignore_editor_change.current)
                return;
            notify_change(selectedLang, value);
        },
        [selectedLang, notify_change]
    );

    if (!langs?.length)
        return null;

    return (
        <div className="TranslatableWysiwyg">

            {langs.length > 1 && (
                <div className="lang-switchers">
                    {langs.map(l => (
                        <button type="button" key={l} onClick={() => select_lang(l)} className={`lang-switcher ${selectedLang === l ? "active" : ""}`}>
                            {l.toUpperCase()}
                        </button>
                    ))}
                </div>
            )}

            <div className="translatable-field-wrapper">
                <CKWysiwyg
                    key={selectedLang}
                    defaultValue={inputText?.[selectedLang]}
                    onChange={editor_on_change}
                    withShortcodes={withShortcodes}
                    withColors={withColors}
                    withImages={withImages}
                    withTables={withTables}
                    readOnly={readOnly}
                    textColor={textColor}
                    linksColor={linksColor}
                />
            </div>

        </div>
    );
}

export default TranslatableWysiwyg;
